// src/components/UnknownAnswersWidget.tsx
import React, { useState } from 'react';
import { UnknownAnswer } from '../types';
import './UnknownAnswersWidget.css';

interface UnknownAnswersWidgetProps {
  unknownAnswers: UnknownAnswer[];
  scoreImprovement?: {
    score: number;
    percentage: number;
    improvement: number;
    estimatedWeeks: number;
  };
}

export const UnknownAnswersWidget: React.FC<UnknownAnswersWidgetProps> = ({
  unknownAnswers,
  scoreImprovement,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!unknownAnswers || unknownAnswers.length === 0) {
    return null;
  }

  // Group by priority
  const byPriority = {
    HIGH: unknownAnswers.filter(a => a.priority === 'HIGH'),
    MEDIUM: unknownAnswers.filter(a => a.priority === 'MEDIUM'),
    LOW: unknownAnswers.filter(a => a.priority === 'LOW'),
  };

  return (
    <div className="unknown-answers-widget">
      <div className="unknown-header">
        <div className="unknown-icon">❓</div>
        <div className="unknown-title-section">
          <h3 className="unknown-title">Knowledge Gaps Found</h3>
          <p className="unknown-subtitle">
            You answered "I don't know" to {unknownAnswers.length} question{unknownAnswers.length > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {scoreImprovement && (
        <div className="score-improvement-box">
          <div className="improvement-metric">
            <div className="improvement-label">Potential Score Improvement</div>
            <div className="improvement-value">
              <span className="current">{scoreImprovement.percentage}%</span>
              <span className="arrow">→</span>
              <span className="improved">{Math.min(100, scoreImprovement.percentage + scoreImprovement.improvement)}%</span>
            </div>
          </div>
          <div className="improvement-timeline">
            <div className="timeline-label">If resolved in</div>
            <div className="timeline-value">{scoreImprovement.estimatedWeeks} weeks</div>
          </div>
        </div>
      )}

      <div className="unknown-sections">
        {/* High Priority */}
        {byPriority.HIGH.length > 0 && (
          <div className="priority-group">
            <div className="priority-header priority-high">
              <span className="priority-badge">CRITICAL</span>
              <span className="priority-count">{byPriority.HIGH.length} item{byPriority.HIGH.length > 1 ? 's' : ''}</span>
            </div>
            <div className="priority-items">
              {byPriority.HIGH.map((unknown) => (
                <UnknownAnswerItem
                  key={unknown.questionId}
                  unknown={unknown}
                  isExpanded={expandedId === unknown.questionId}
                  onToggle={() =>
                    setExpandedId(expandedId === unknown.questionId ? null : unknown.questionId)
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Medium Priority */}
        {byPriority.MEDIUM.length > 0 && (
          <div className="priority-group">
            <div className="priority-header priority-medium">
              <span className="priority-badge">HIGH PRIORITY</span>
              <span className="priority-count">{byPriority.MEDIUM.length} item{byPriority.MEDIUM.length > 1 ? 's' : ''}</span>
            </div>
            <div className="priority-items">
              {byPriority.MEDIUM.map((unknown) => (
                <UnknownAnswerItem
                  key={unknown.questionId}
                  unknown={unknown}
                  isExpanded={expandedId === unknown.questionId}
                  onToggle={() =>
                    setExpandedId(expandedId === unknown.questionId ? null : unknown.questionId)
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Low Priority */}
        {byPriority.LOW.length > 0 && (
          <div className="priority-group">
            <div className="priority-header priority-low">
              <span className="priority-badge">MEDIUM PRIORITY</span>
              <span className="priority-count">{byPriority.LOW.length} item{byPriority.LOW.length > 1 ? 's' : ''}</span>
            </div>
            <div className="priority-items">
              {byPriority.LOW.map((unknown) => (
                <UnknownAnswerItem
                  key={unknown.questionId}
                  unknown={unknown}
                  isExpanded={expandedId === unknown.questionId}
                  onToggle={() =>
                    setExpandedId(expandedId === unknown.questionId ? null : unknown.questionId)
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="unknown-cta">
        <button className="cta-button">
          Create Action Plan for Knowledge Gaps
        </button>
        <p className="cta-text">
          We can help you research and answer these questions
        </p>
      </div>
    </div>
  );
};

// Sub-component for individual unknown answer
interface UnknownAnswerItemProps {
  unknown: UnknownAnswer;
  isExpanded: boolean;
  onToggle: () => void;
}

const UnknownAnswerItem: React.FC<UnknownAnswerItemProps> = ({
  unknown,
  isExpanded,
  onToggle,
}) => {
  return (
    <div className="unknown-item">
      <button className="unknown-item-header" onClick={onToggle}>
        <div className="unknown-item-content">
          <h4 className="unknown-question">{unknown.questionText}</h4>
          <p className="unknown-section">{unknown.section}</p>
        </div>
        <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          ▶
        </div>
      </button>

      {isExpanded && (
        <div className="unknown-item-details">
          <div className="investigation-guide">
            <h5>How to Find the Answer</h5>
            
            {unknown.investigationGuide.whoToAsk.length > 0 && (
              <div className="guide-section">
                <strong>Who to Ask:</strong>
                <ul>
                  {unknown.investigationGuide.whoToAsk.map((person, idx) => (
                    <li key={idx}>{person}</li>
                  ))}
                </ul>
              </div>
            )}

            {unknown.investigationGuide.whereToLook.length > 0 && (
              <div className="guide-section">
                <strong>Where to Look:</strong>
                <ul>
                  {unknown.investigationGuide.whereToLook.map((place, idx) => (
                    <li key={idx}>{place}</li>
                  ))}
                </ul>
              </div>
            )}

            {unknown.investigationGuide.howToVerify && (
              <div className="guide-section">
                <strong>How to Verify:</strong>
                <p>{unknown.investigationGuide.howToVerify}</p>
              </div>
            )}

            {unknown.investigationGuide.whatToDocument && (
              <div className="guide-section">
                <strong>What to Document:</strong>
                <p>{unknown.investigationGuide.whatToDocument}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
